import { useEffect, useState } from 'react'
import { LayoutDashboard } from 'lucide-react'
import { getDashboard, listDashboards } from '~/api/monitoring'
import { useStats } from '~/api/use-stats'
import type { AdminDashboard } from '~/api/types'
import {
    ChartCard,
    DonutChart,
    KRDS,
    type DonutDatum,
} from '~/components/charts'
import { DashboardDetailPanel } from '~/components/DashboardDetailPanel'
import { DashboardTable } from '~/components/DashboardTable'
import { FeedbackPanel } from '~/components/FeedbackPanel'
import { MonitoringRiskPanel } from '~/components/MonitoringRiskPanel'
import { ServicePageShell } from '~/components/ServicePageShell'
import { StatCard } from '~/components/StatCard'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { cn, formatDateTime, whoLabel } from '~/lib/utils'

// 모니터링 서비스 페이지. 개요(KPI + 알림 현황 + 대시보드 목록) + 데이터(목록 → 상세) 탭.
export function MonitoringPage() {
    const toast = useToast()
    const { stats, error } = useStats()
    const [selected, setSelected] = useState<AdminDashboard | null>(null)
    const [recent, setRecent] = useState<AdminDashboard[] | null>(null)
    // 최신 대시보드 1개의 측정값 규모(상세 호출).
    const [latestReadings, setLatestReadings] = useState<number | null>(null)
    const m = stats?.monitoring

    // 통계 조회 오류는 토스트로 알린다.
    useEffect(() => {
        if (error) toast.error(error)
    }, [error, toast])

    useEffect(() => {
        let alive = true
        listDashboards({ limit: 5 })
            .then((rows) => {
                if (!alive) return
                setRecent(rows)
                const first = rows[0]
                if (first) {
                    getDashboard(first.id)
                        .then(
                            (d) => alive && setLatestReadings(d.readingsCount)
                        )
                        .catch(() => {})
                }
            })
            .catch(() => alive && setRecent([]))
        return () => {
            alive = false
        }
    }, [])

    // 알림 현황: 미확인(danger) vs 확인(gray).
    const unread = m?.alertsUnread ?? 0
    const total = m?.alertsTotal ?? 0
    const acked = Math.max(0, total - unread)
    const alertDist: DonutDatum[] = [
        { name: '미확인', value: unread, color: KRDS.danger },
        { name: '확인', value: acked, color: KRDS.gray[30] },
    ]
    const alertsEmpty = !m || total === 0

    const overview = (
        <>
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                <StatCard
                    label='대시보드'
                    value={m?.dashboards ?? '-'}
                    tone='primary'
                />
                <StatCard
                    label='측정값'
                    value={m?.readings ?? '-'}
                    tone='info'
                />
                <StatCard
                    label='미확인 알림'
                    value={m?.alertsUnread ?? '-'}
                    hint={`전체 ${m?.alertsTotal ?? '-'}건`}
                    tone='danger'
                />
                <StatCard label='전체 알림' value={m?.alertsTotal ?? '-'} />
            </div>

            <div className='mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2'>
                <ChartCard
                    title='알림 현황'
                    description='미확인 대비 확인 알림 비중'
                    empty={alertsEmpty}
                    emptyText='알림이 없어요.'>
                    <DonutChart
                        data={alertDist}
                        centerValue={total}
                        centerLabel='전체 알림'
                    />
                </ChartCard>

                <Card className='p-5'>
                    <div className='mb-3 flex items-baseline justify-between gap-2'>
                        <h3 className='krds-title text-foreground'>
                            대시보드 목록
                        </h3>
                        {latestReadings !== null && recent && recent[0] ? (
                            <span className='text-muted-foreground text-[13px]'>
                                최신 {recent[0].name} · 측정값 {latestReadings}
                                건
                            </span>
                        ) : null}
                    </div>
                    {recent !== null && recent.length === 0 ? (
                        <EmptyState
                            icon={LayoutDashboard}
                            title='대시보드가 없어요.'
                            description='모니터링 대시보드가 생성되면 여기에 표시돼요.'
                        />
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>이름</TableHead>
                                    <TableHead>사용자</TableHead>
                                    <TableHead>평가 ID</TableHead>
                                    <TableHead>생성일</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {recent === null
                                    ? Array.from({ length: 5 }).map((_, i) => (
                                          <TableRow key={i}>
                                              <TableCell>
                                                  <Skeleton className='h-4 w-40' />
                                              </TableCell>
                                              <TableCell>
                                                  <Skeleton className='h-4 w-28' />
                                              </TableCell>
                                              <TableCell>
                                                  <Skeleton className='h-4 w-16' />
                                              </TableCell>
                                              <TableCell>
                                                  <Skeleton className='h-4 w-28' />
                                              </TableCell>
                                          </TableRow>
                                      ))
                                    : recent.map((d) => (
                                          <TableRow
                                              key={d.id}
                                              onClick={() => setSelected(d)}
                                              className={cn(
                                                  'cursor-pointer',
                                                  d.id === selected?.id &&
                                                      'bg-accent hover:bg-accent'
                                              )}>
                                              <TableCell className='font-bold'>
                                                  {d.name}
                                              </TableCell>
                                              <TableCell
                                                  className='max-w-[180px] truncate'
                                                  title={
                                                      d.ownerEmail ?? undefined
                                                  }>
                                                  {whoLabel(
                                                      d.ownerName,
                                                      d.ownerEmail
                                                  )}
                                              </TableCell>
                                              <TableCell className='font-mono text-xs'>
                                                  {d.assessmentId
                                                      ? d.assessmentId.slice(
                                                            0,
                                                            8
                                                        )
                                                      : '-'}
                                              </TableCell>
                                              <TableCell className='whitespace-nowrap'>
                                                  {formatDateTime(d.createdAt)}
                                              </TableCell>
                                          </TableRow>
                                      ))}
                            </TableBody>
                        </Table>
                    )}
                </Card>
            </div>
        </>
    )

    const risk = (
        <MonitoringRiskPanel onSelect={setSelected} selectedId={selected?.id} />
    )

    const data = (
        <DashboardTable onSelect={setSelected} selectedId={selected?.id} />
    )

    return (
        <>
            <ServicePageShell
                title='모니터링'
                description='실시간 모니터링 서비스 개요'
                tabs={[
                    { label: '개요', content: overview },
                    { label: '위험 관제', content: risk },
                    { label: '데이터', content: data },
                    {
                        label: '피드백',
                        content: <FeedbackPanel service='monitoring' />,
                    },
                ]}
            />
            {selected ? (
                <DashboardDetailPanel
                    dashboardId={selected.id}
                    onClose={() => setSelected(null)}
                />
            ) : null}
        </>
    )
}
